import React, { useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  STUDENT_NOTES_QUERY, ADD_TEACHER_NOTE_MUTATION, UPDATE_TEACHER_NOTE_MUTATION,
  DELETE_TEACHER_NOTE_MUTATION, STUDENT_SAY_ITEMS_QUERY,
} from '../query/gqlQueries.js'

// The teacher's side of steering practice, on the student dashboard:
//   • notes — plain text on what the student struggles with or should
//     practise, in the language being shown. The student sees them as
//     homework and can have a practice lesson written from any note.
//   • what they want to say — the student's own list, so a lesson can be
//     built round what they actually want to talk about.
// Styling comes from the dashboard's classes and theme variables.

const fmt = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

const box = {
  width: '100%', boxSizing: 'border-box', minHeight: 90, resize: 'vertical',
  background: 'var(--surface, var(--frost-field))', color: 'var(--text, var(--frost-text))',
  border: '1px solid var(--border, var(--frost-border))', borderRadius: 12, padding: '0.75rem 0.9rem',
  font: 'inherit', fontSize: '0.95rem', lineHeight: 1.5,
}
const card = {
  background: 'var(--surface, var(--frost-field))', border: '1px solid var(--border, var(--frost-border))',
  borderRadius: 12, padding: '0.8rem 1rem',
}
const linkBtn = { background: 'none', border: 'none', color: 'var(--muted, var(--frost-muted))', cursor: 'pointer', font: 'inherit', fontSize: '0.82rem', padding: 0 }

// `part`: 'notes' or 'say' for one of the two (the dashboard puts them in
// different places); both when left out. `sayLimit`: how many of their
// phrases to show before "Show all".
export default function StudentNotes({ student, lang, langName, styles, part, sayLimit = Infinity }) {
  const [allSay, setAllSay] = useState(false)
  const on = !!(student && lang)
  const notesQ = useQuery(STUDENT_NOTES_QUERY, { variables: { student, lang }, skip: !on, fetchPolicy: 'cache-and-network', errorPolicy: 'all' })
  const sayQ = useQuery(STUDENT_SAY_ITEMS_QUERY, { variables: { student, lang }, skip: !on, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore' })
  const [addNote] = useMutation(ADD_TEACHER_NOTE_MUTATION)
  const [updateNote] = useMutation(UPDATE_TEACHER_NOTE_MUTATION)
  const [deleteNote] = useMutation(DELETE_TEACHER_NOTE_MUTATION)

  const [draft, setDraft] = useState('')
  const [editing, setEditing] = useState(null) // { id, text }
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)

  if (!on) return null
  const notes = notesQ.data?.studentNotes || []
  const sayItems = sayQ.data?.studentSayItems || []
  const backendOld = /Cannot query field|Unknown type/i.test(notesQ.error?.message || '')

  const run = async (fn) => {
    setSaving(true); setErr('')
    try { await fn(); await notesQ.refetch() } catch (e) { setErr(e.message || 'That did not save.') }
    setSaving(false)
  }
  const save = () => run(async () => {
    await addNote({ variables: { student, lang, text: draft } })
    setDraft('')
  })
  const saveEdit = () => run(async () => {
    await updateNote({ variables: { id: editing.id, text: editing.text } })
    setEditing(null)
  })
  const remove = (id) => run(async () => {
    await deleteNote({ variables: { id } })
    setConfirmDelete(null)
  })

  const showNotes = part !== 'say'
  const showSay = part !== 'notes'
  const sayShown = allSay ? sayItems : sayItems.slice(0, sayLimit)
  return (
    <>
      {showNotes && <div className={styles.section}>
        <div className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Notes for {student}</h2>
          <span className={styles.sectionNote}>what to work on in {langName} — {student} sees these as homework and can practise from them</span>
        </div>
        {backendOld
          ? <div className={styles.err}>The GraphQL server hasn't been redeployed with teacher notes yet.</div>
          : (
            <>
              <textarea
                style={box}
                placeholder={`What should ${student} work on in ${langName}? They see it as homework and can practise from it.`}
                value={draft}
                onChange={e => setDraft(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '0.7rem', alignItems: 'center', margin: '0.6rem 0 1rem' }}>
                <button className={styles.primaryBtn} disabled={!draft.trim() || saving} onClick={save}>Save note</button>
                {err && <span className={styles.err}>{err}</span>}
              </div>
              {notes.length === 0 && !notesQ.loading && <div className={styles.empty}>No notes about {student} in {langName} yet.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {notes.map(n => (
                  <div key={n.id} style={card}>
                    {editing?.id === n.id ? (
                      <>
                        <textarea style={box} value={editing.text} onChange={e => setEditing({ ...editing, text: e.target.value })} />
                        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.5rem' }}>
                          <button className={styles.primaryBtn} disabled={!editing.text.trim() || saving} onClick={saveEdit}>Save</button>
                          <button className={styles.ghostBtn} onClick={() => setEditing(null)}>Cancel</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text, var(--frost-text))', lineHeight: 1.5 }}>{n.text}</div>
                        <div style={{ display: 'flex', gap: '0.9rem', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                          <span className={styles.sectionNote}>{fmt(n.updatedAt || n.createdAt)}{n.lessonStatus === 'ready' ? ' · practice lesson written' : ''}</span>
                          <button style={linkBtn} onClick={() => setEditing({ id: n.id, text: n.text })}>Edit</button>
                          {confirmDelete === n.id
                            ? <>
                              <span className={styles.sectionNote}>Delete this note?</span>
                              <button style={{ ...linkBtn, color: 'var(--error, var(--frost-wrong))' }} onClick={() => remove(n.id)}>Delete</button>
                              <button style={linkBtn} onClick={() => setConfirmDelete(null)}>Keep</button>
                            </>
                            : <button style={linkBtn} onClick={() => setConfirmDelete(n.id)}>Delete</button>}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
      </div>}

      {showSay && sayItems.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>What {student} wants to say</h2>
            <span className={styles.sectionNote}>{sayItems.length} of their own phrases — good material for a lesson</span>
          </div>
          <div className={styles.sayList || ''} style={styles.sayList ? undefined : { display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {sayShown.map(s => (
              <div key={s.id} className={styles.sayItem || ''} style={styles.sayItem ? undefined : card}>
                <div style={{ color: 'var(--text, var(--frost-text))', fontWeight: 600 }}>{s.translation || '—'}</div>
                <div className={styles.sectionNote} style={{ marginTop: '0.2rem' }}>{s.text}</div>
              </div>
            ))}
          </div>
          {sayItems.length > sayShown.length && <button style={{ ...linkBtn, marginTop: '0.6rem' }} onClick={() => setAllSay(true)}>Show all {sayItems.length} →</button>}
          {allSay && sayItems.length > sayLimit && <button style={{ ...linkBtn, marginTop: '0.6rem' }} onClick={() => setAllSay(false)}>Show fewer</button>}
        </div>
      )}
    </>
  )
}
